import {Observable, Subscriber} from "rxjs";
import {ApiClientService} from "../api-client/api-client.service";
import {ApiResponse} from "../../api/api-response";
import {AbstractModel} from "../../models/abstract-model";
import {TableSorter} from "../../tools/table-sorter/table-sorter";
import {Paginator} from "../../tools/pagination/paginator";
import {TableFilter} from '../../tools/table-filter/table-filter';

export abstract class AbstractModelService<TModel extends AbstractModel> {
  protected constructor(
    protected apiClient: ApiClientService,
    protected routePath: string,
    protected readonly _createClass: { new(): TModel }
  ) {
  }

  findAll(
    params: any = {},
    paginator: Paginator | null = null,
    tableSorter: TableSorter | null = null,
    tableFilter: TableFilter | null = null
  ): Observable<TModel[]> {
    return new Observable<TModel[]>((subscriber) => {
      if (paginator) {
        params.page = paginator.currentPage;
        params.count = paginator.countByPage;
      }

      if (tableSorter) {
        params.sort = tableSorter.sortName;
        params.order = tableSorter.sortOrder;
      }

      if (tableFilter) {
        const filters = tableFilter.getFilters();
        for (const name in filters) {
          params[name] = filters[name];
        }
      }

      this.apiClient.get(this.routePath, params).subscribe({
        next: (response: ApiResponse) => {
          let models = response.getData().map((model: any) => new this._createClass().deserialize(model));

          if (paginator) {
            paginator.count = response.getTotal();
          }

          subscriber.next(models);
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  findOneById(id: number, params: any = {}): Observable<TModel> {
    return new Observable<TModel>((subscriber) => {
      this.apiClient.get(this.routePath + '/' + id, params).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(new this._createClass().deserialize(response.getData()));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  create(data: any = {}): Observable<any> {
    return new Observable<void>((subscriber: Subscriber<any>): void => {
      this.apiClient.post(this.routePath, {}, data).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData());
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  update(id: number, data: any = {}): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>): void => {
      this.apiClient.put(this.routePath + '/' + id, {}, data).subscribe({
        next: () => {
          subscriber.next();
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  delete(id: number, params: any = {}): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>): void => {
      this.apiClient.delete(this.routePath + '/' + id, params).subscribe({
        next: () => {
          subscriber.next();
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getUrl(id: number): Observable<string> {
    return new Observable<string>((subscriber) => {
      this.apiClient.get(this.routePath + '/' + id + '/url').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData()['url']);
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }
}
